<template>
    <section name="pagination" class="gap-xs flex items-center justify-end">
        <div class="overflow-x-auto whitespace-nowrap">
            <button
                v-show="page !== 1"
                class="rounded-sm border border-transparent hover:border-(--btn-outline-fg) hover:text-(--text-on-accent)"
                @click="decrement"
            >
                <i class="i-arrow-left block" />
            </button>

            <template v-for="n in displayedPages" :key="n ?? 'ellipsis'">
                <button
                    v-if="n !== null"
                    class="p2 font-matter rounded-sm"
                    :class="{
                        'border border-(--btn-outline-fg) text-(--link-default)': n == page,
                        'hover:bg-(--btn-primary-bg) hover:text-(--text-on-accent)': n !== page,
                    }"
                    @click="setPage(n)"
                >
                    {{ n }}
                </button>

                <button
                    v-else
                    class="p2 font-matter cursor-pointer rounded-sm hover:bg-(--btn-primary-bg) hover:text-(--text-on-accent)"
                    @click="showAll"
                >
                    ...
                </button>
            </template>

            <button
                v-show="page !== totalPages"
                class="rounded-sm border border-transparent hover:border-(--btn-outline-fg) hover:text-(--text-on-accent)"
                @click="increment"
            >
                <i class="i-arrow-left block rotate-180" />
            </button>
        </div>
    </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

const props = defineProps({
    page: {
        type: Number,
        required: true,
    },
    totalPages: {
        type: Number,
        required: true,
    },
});

const emit = defineEmits(['update:page']);

const page = computed({
    get: () => props.page,
    set: (value: number) => emit('update:page', value),
});

const showAllPages = ref(false);

function setPage(value: number) {
    page.value = value;
}

function decrement() {
    page.value--;
}

function showAll() {
    showAllPages.value = true;
}

const displayedPages = computed(() => {
    if (showAllPages.value || props.totalPages <= 5) {
        return Array.from({ length: props.totalPages }, (_, i) => i + 1);
    }

    const pages: (number | null)[] = [1, 2, 3];

    if (props.totalPages > 4) {
        pages.push(null); // ellipsis
        pages.push(props.totalPages);
    }

    return pages;
});

const increment = () => {
    if (page.value === 3) {
        showAllPages.value = true;
    }
    if (page.value < props.totalPages) {
        page.value++;
    }
};
</script>
